/**
 * LeadResetPassword.js
 *
 * Works in both environments:
 *   - client/       (web, React Router): onDone prop OR window.location.href fallback
 *   - client-lead/  (Capacitor mobile):  onDone prop passed by App.js state machine
 *
 * NO react-router-dom dependency — safe for client-lead which doesn't have it.
 */
import React, { useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '';

export default function LeadResetPassword({ onDone, onLogout }) {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirm] = useState('');
  const [error, setError]             = useState('');
  const [loading, setLoading]         = useState(false);

  const token = localStorage.getItem('leadToken');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }
    if (newPassword.length < 8)          { setError('Password must be at least 8 characters'); return; }

    setLoading(true);
    try {
      const resp = await axios.post(
        `${API}/api/lead-auth/reset-password`,
        { newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      localStorage.setItem('leadToken', resp.data.token);

      // Prefer onDone callback (mobile state machine, or web parent)
      // Fall back to hard redirect for web when no callback is provided
      if (onDone) {
        onDone();
      } else {
        window.location.href = '/lead/dashboard';
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={s.bg}>
      <div style={s.card}>
        <div style={s.header}>
          <div style={s.icon}>🔑</div>
          <h2 style={s.title}>Set Your Password</h2>
          <p style={s.sub}>Choose a strong password to secure your account.</p>
        </div>

        {error && <div style={s.errorBox}>{error}</div>}

        <form onSubmit={handleSubmit} style={s.form}>
          <div style={s.field}>
            <label style={s.label}>New Password</label>
            <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
              required style={s.input} placeholder="Min. 8 characters" />
          </div>
          <div style={s.field}>
            <label style={s.label}>Confirm Password</label>
            <input type="password" value={confirmPassword} onChange={e => setConfirm(e.target.value)}
              required style={s.input} placeholder="Repeat password" />
          </div>
          <button type="submit" disabled={loading} style={s.btn}>
            {loading ? 'Saving…' : 'Save Password & Continue'}
          </button>
        </form>

        {onLogout && (
          <button onClick={onLogout} style={s.logoutBtn}>Cancel & Logout</button>
        )}
      </div>
    </div>
  );
}

const s = {
  bg:        { minHeight: '100dvh', background: 'linear-gradient(135deg, #1a1f36 0%, #2d3561 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' },
  card:      { background: '#fff', borderRadius: '16px', padding: '40px', width: '100%', maxWidth: '400px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' },
  header:    { textAlign: 'center', marginBottom: '28px' },
  icon:      { fontSize: '44px', marginBottom: '12px' },
  title:     { margin: 0, fontSize: '22px', fontWeight: 700, color: '#1a1f36' },
  sub:       { margin: '8px 0 0', color: '#666', fontSize: '14px' },
  errorBox:  { background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', padding: '12px', color: '#dc2626', fontSize: '14px', marginBottom: '16px' },
  form:      { display: 'flex', flexDirection: 'column', gap: '18px' },
  field:     { display: 'flex', flexDirection: 'column', gap: '6px' },
  label:     { fontSize: '14px', fontWeight: 600, color: '#374151' },
  input:     { border: '1px solid #d1d5db', borderRadius: '8px', padding: '11px 14px', fontSize: '15px', outline: 'none' },
  btn:       { background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)', color: '#fff', border: 'none', borderRadius: '8px', padding: '13px', fontSize: '16px', fontWeight: 600, cursor: 'pointer' },
  logoutBtn: { marginTop: '16px', background: 'none', border: 'none', color: '#999', fontSize: '13px', cursor: 'pointer', width: '100%', textAlign: 'center' },
};
